import { router } from "@inertiajs/react";
import { useState } from "react";
import { toast } from "sonner";
import { CheckStatusBadge } from "@/components/check-status-badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";

interface CheckCardProps {
	checked: boolean;
	checkedAt: string | null;
	checkExpiresAt: string | null;
	checkUrl: string;
	uncheckUrl: string;
}

export function CheckCard({
	checked,
	checkedAt,
	checkExpiresAt,
	checkUrl,
	uncheckUrl,
}: CheckCardProps) {
	const [checkDate, setCheckDate] = useState<string | null>(
		new Date().toISOString().split("T")[0],
	);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const oneYearAgo = new Date(today);
	oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
	oneYearAgo.setDate(oneYearAgo.getDate() + 1); // Exclude exactly 1 year ago (would be expired)

	const handleCheck = () => {
		router.post(
			checkUrl,
			{ checked_at: checkDate },
			{
				preserveScroll: true,
				onSuccess: () => {
					toast.success("Datum kontroly bylo uloženo.");
				},
				onError: () => {
					toast.error("Nepodařilo se uložit datum kontroly.");
				},
			},
		);
	};

	const handleUncheck = () => {
		router.post(
			uncheckUrl,
			{},
			{
				preserveScroll: true,
				onSuccess: () => {
					toast.success("Kontrola byla úspěšně smazána.");
				},
				onError: () => {
					toast.error("Nepodařilo se smazat kontrolu.");
				},
			},
		);
	};

	if (checked && checkedAt) {
		return (
			<div className="rounded-lg border p-4">
				<div className="flex flex-col items-center justify-between gap-3">
					<div className="w-full">
						<div className="flex items-center justify-between gap-2">
							<span className="font-medium text-sm">Kontrola</span>
							<CheckStatusBadge
								checked={true}
								checkExpiresAt={checkExpiresAt}
							/>
						</div>
						<div className="mt-3">
							<table className="w-full text-muted-foreground text-sm">
								<tbody>
									<tr>
										<td className="py-1 pr-4">Zkontrolováno dne</td>
										<td className="py-1 text-right font-medium">
											{new Date(checkedAt).toLocaleDateString("cs-CZ")}
										</td>
									</tr>
									{checkExpiresAt && (
										<tr>
											<td className="py-1 pr-4">Platnost do</td>
											<td className="py-1 text-right font-medium">
												{new Date(checkExpiresAt).toLocaleDateString("cs-CZ")}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-full"
						onClick={handleUncheck}
					>
						Smazat
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="rounded-lg border p-4">
			<div className="flex items-center justify-between gap-2">
				<span className="font-medium text-sm">Kontrola</span>
				<CheckStatusBadge checked={false} />
			</div>
			<div className="mt-3 flex flex-col gap-3">
				<div className="flex items-center justify-between gap-4">
					<label className="line-clamp-1 text-muted-foreground text-sm">
						Datum kontroly
					</label>
					<DatePicker
						value={checkDate}
						onChange={setCheckDate}
						placeholder="Vyberte datum"
						fromDate={oneYearAgo}
						toDate={today}
						className="w-auto shrink"
					/>
				</div>

				<Button type="button" onClick={handleCheck} disabled={!checkDate}>
					Uložit datum kontroly
				</Button>
			</div>
		</div>
	);
}
